import React, { createContext, useState, useContext, useEffect } from 'react';
import { FlatCardData, ParkingCardData, BusinessCardData } from '../../components/CardsComponents/types';

interface FavoriteContextProps {
  favorites: (FlatCardData | ParkingCardData | BusinessCardData)[];
  addToFavorites: (card: FlatCardData | ParkingCardData | BusinessCardData) => void;
  removeFromFavorites: (card: FlatCardData | ParkingCardData | BusinessCardData) => void;
  toggleFavorite: (card: FlatCardData | ParkingCardData | BusinessCardData) => void;
  removeAllByType: (type: 'flat' | 'parking' | 'business') => void;
  lastAddedProperty: FlatCardData | ParkingCardData | BusinessCardData | null;
  resetLastAddedProperty: () => void;
}

const FavoriteContext = createContext<FavoriteContextProps | undefined>(undefined);

export const FavoriteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<(FlatCardData | ParkingCardData | BusinessCardData)[]>(() => {
    const storedFavorites = localStorage.getItem('favorites');
    return storedFavorites ? JSON.parse(storedFavorites) : [];
  });
  const [lastAddedProperty, setLastAddedProperty] = useState<FlatCardData | ParkingCardData | BusinessCardData | null>(null);
  const [updateCounter, setUpdateCounter] = useState(0);

  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  const resetLastAddedProperty = () => {
    setLastAddedProperty(null);
  };

  const addToFavorites = (card: FlatCardData | ParkingCardData | BusinessCardData) => {
    setFavorites((prevFavorites) => {
      if (!prevFavorites.some((item) => item.id === card.id && item.type === card.type)) {
        setLastAddedProperty(card);
        setUpdateCounter(prev => prev + 1);
        return [...prevFavorites, card];
      }
      return prevFavorites;
    });
  };

  const removeFromFavorites = (card: FlatCardData | ParkingCardData | BusinessCardData) => {
    if (lastAddedProperty && lastAddedProperty.id === card.id && lastAddedProperty.type === card.type) {
      setLastAddedProperty(null);
    }
    
    setFavorites((prevFavorites) =>
      prevFavorites.filter((item) => item.id !== card.id || item.type !== card.type)
    );
  };

  // Функция для добавления/удаления из избранного
  const toggleFavorite = (card: FlatCardData | ParkingCardData | BusinessCardData) => {
    // Проверяем, есть ли объект уже в избранном
    const isFavorite = favorites.some((item) => item.id === card.id && item.type === card.type);
    
    if (isFavorite) {
      // Если это тот же объект, что был последним добавленным, то сбрасываем его,
      // но сначала сообщаем компонентам тоста, что объект был в избранном и удаляется
      if (lastAddedProperty && lastAddedProperty.id === card.id && lastAddedProperty.type === card.type) {
        // Устанавливаем тот же объект снова, чтобы компоненты тостов отреагировали 
        // на удаление из избранного - это заставит их проверить статус объекта 
        // и скрыть тост, так как объект будет удален
        setLastAddedProperty({...card});
      } else {
        // Если это другой объект, просто сбрасываем lastAddedProperty
        setLastAddedProperty(null);
      }
      
      // Удаляем из избранного
      setFavorites(prevFavorites => 
        prevFavorites.filter((item) => item.id !== card.id || item.type !== card.type)
      );
    } else {
      // Если объекта нет в избранном, добавляем его
      setLastAddedProperty(card);
      setUpdateCounter(prev => prev + 1);
      setFavorites(prevFavorites => [...prevFavorites, card]);
    }
  };

  const removeAllByType = (type: 'flat' | 'parking' | 'business') => {
    if (lastAddedProperty && lastAddedProperty.type === type) {
      setLastAddedProperty(null);
    }
    
    setFavorites((prevFavorites) =>
      prevFavorites.filter((item) => item.type !== type)
    );
  };

  return (
    <FavoriteContext.Provider
      value={{ 
        favorites, 
        addToFavorites, 
        removeFromFavorites, 
        toggleFavorite, 
        removeAllByType, 
        lastAddedProperty,
        resetLastAddedProperty
      }}
    >
      {children}
    </FavoriteContext.Provider>
  );
};

export const useFavorite = () => {
  const context = useContext(FavoriteContext);
  if (!context) {
    throw new Error('useFavorite must be used within a FavoriteProvider');
  }
  return context;
};
